import * as React from "react";
import { withTranslation, WithTranslation, useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { I18N_NS } from "Config/i18n";
import { ScrollableTable, TableColumnProps } from "Components/ScrollableTable/ScrollableTable";
import { FormatData } from "utils/FormatData";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Row, Tooltip, Col } from "antd";
import { documentsTableStore } from "Pages/Project/Documents/Stores/DocumentsTableStore";
import { onlineViewerStore } from "Stores/OnlineViewerStore";
import { wopiViewerStore } from "Stores/WOPIViewerStore";
import { pdfViewerStore } from "Stores/PdfViewerStore";
import { TaskUploadFile } from "../Stores/TaskDataStore";
import { TaskFilesTableItemActions } from "./TaskFilesTableItemActions";
import { TaskFilesTableActions } from "./TaskFilesTableActions";
import { ModalConsumer } from "Components/General/Modal/ModalContext";
import { Button } from "Components/General/Button";
import { AttachProjectFiles } from "./AttachProjectFiles";
import { WithModal } from "Components/General/Modal/withModal";
import { BCFViewerModal } from "Components/BcfViewer/BCFViewerModal";
import { modalProps } from "Pages/Project/QA/utils/modalProps";
import classnames from "classnames";

const FilenameCell = ({ file }: { file: TaskUploadFile }) => {
  const { t } = useTranslation(I18N_NS.PROJECT);
  if (file.status === "uploading") {
    return (
      <span data-testid="task-files-row-name">
        {file.name} <FontAwesomeIcon icon={["far", "spinner"]} spin={true} style={{ marginLeft: 5 }} />
      </span>
    );
  } else if (file.status === "error") {
    return (
      <Tooltip title={t("Failed to upload file. Please, remove file from the list and upload it again.")}>
        <span className="color-red" data-testid="task-files-row-name">
          {file.name} <FontAwesomeIcon icon={["far", "times-circle"]} style={{ marginLeft: 5 }} />
        </span>
      </Tooltip>
    );
  }
  return <span data-testid="task-files-row-name">{file.name}</span>;
};

interface TaskFilesTableProps extends WithTranslation, WithModal {
  data: TaskUploadFile[];
  loading?: boolean;
  removeFile: (uid: string[]) => Promise<void>;
  addFile: (v: API_TaskRequestData) => Promise<void>;
  withButton: boolean;
  canRemoveFile: boolean;
  editMode?: boolean;
}

interface TaskFilesTableState {
  selectedFiles: TaskUploadFile[];
  selectedFileKeys: string[];
}

export const TaskFilesTable = withTranslation(I18N_NS.PROJECT)(
  observer(
    class extends React.Component<TaskFilesTableProps, TaskFilesTableState> {
      constructor(props: TaskFilesTableProps) {
        super(props);
        this.state = {
          selectedFiles: [],
          selectedFileKeys: [],
        };
      }

      resetSelection = () => this.setState({ selectedFiles: [] });

      removeSelectedFiles = () => {
        const { selectedFileKeys } = this.state;
        const ids = selectedFileKeys.filter((uid) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          if (!isNaN(uid as any)) {
            return `@id${uid}`;
          }
          return false;
        });
        this.props.removeFile(ids);
        this.setState({ selectedFileKeys: [], selectedFiles: [] });
      };

      removeSingleFile = (fileId) => {
        const fileIndex = this.state.selectedFileKeys.findIndex((item) => item === fileId);
        if (fileIndex !== -1) {
          const { selectedFileKeys, selectedFiles } = this.state;
          selectedFileKeys.splice(fileIndex, 1);
          selectedFiles.splice(fileIndex, 1);
          this.setState({ selectedFileKeys: [], selectedFiles: [] });
        }
        this.props.removeFile([fileId]);
      };

      handleSelectRows = ({ selectedRows, selectedRowKeys }) => {
        this.setState({ selectedFileKeys: selectedRowKeys, selectedFiles: selectedRows });
      };

      renderFileType = ({ rowData: file }: { rowData: TaskUploadFile }) => {
        // consider that if file was added from project - it would always have "parentId" prop
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((!file as any as API_FileDataEntity).parentId) {
          return file.type;
        } else {
          // consider that extension is in the last part after dot
          const splittedFileName = file.name.split(".");
          if (splittedFileName.length > 1) {
            // if filename has extension - length would be > 1
            return splittedFileName[splittedFileName.length - 1];
          } else {
            return "";
          }
        }
      };

      downloadSelectedFiles = () => {
        if (!this.state.selectedFiles.length) {
          return;
        }
        documentsTableStore.downloadFiles(
          this.state.selectedFiles.map((data) => ({ id: +data.id, name: data.name, fileActions: data.fileActions }))
        );
      };

      onSingleActionSelect = (key: string, data: TaskUploadFile) => {
        const { t, showModal } = this.props;

        switch (key) {
          case "download":
            documentsTableStore.downloadFiles([{ id: +data.id, name: data.name, fileActions: data.fileActions }]);
            break;
          case "bcf_view":
            showModal(BCFViewerModal, {
              modalProps: {
                ...modalProps,
                maskClosable: true,
                width: "100%",
                title: `${t("File")}: ${data.name}`,
                style: { maxWidth: "1300px" },
                className: "full-height-modal with-drawer bcf-modal",
                zIndex: 1013,
              },
              componentProps: { fileId: +data.id },
            });
            break;
          case "easy_view":
            pdfViewerStore.fetch(+data.id, {
              fileName: data.name,
              canDownload: data.fileActions.download || data.fileActions.downloadWithWatermark,
            });
            break;
          case "wopi_view":
            wopiViewerStore.fetch(+data.id, { fileName: data.name, edit: data.viewers.office365Edit });
            break;
          case "online_view":
            onlineViewerStore.fetch(+data.id, { fileName: data.name });
            break;
          case "remove":
            this.removeSingleFile(data.uid);
            break;
          default:
            break;
        }
      };

      getRowClassName = ({ deleted }: TaskUploadFile) => {
        return classnames({ disabledCell: deleted });
      };

      get tableActions() {
        const config = {
          files: this.state.selectedFiles,
          onDownload: () => this.downloadSelectedFiles(),
          disabledDownload: this.selectedFilesCanNotBeDownloaded,
          onRemoveSelectedFiles: this.props.canRemoveFile ? this.removeSelectedFiles : null,
        };
        return config;
      }

      get selectedFilesCanNotBeDownloaded() {
        return this.state.selectedFiles.some((file) => !file.fileActions || !file.fileActions.download);
      }

      render() {
        const { t, data, withButton } = this.props;
        const columns: TableColumnProps<TaskUploadFile>[] = [
          // { dataKey: 'uid', label: t('uid'), width: 100 },
          {
            dataKey: "name",
            label: t("Filename"),
            flexGrow: 1,
            width: 140,
            cellRenderer: ({ rowData }) => <FilenameCell file={rowData} />,
          },
          {
            dataKey: "type",
            label: t("Type"),
            width: 70,
            cellRenderer: this.renderFileType,
          },
          {
            dataKey: "size",
            label: t("Size"),
            width: 80,
            cellRenderer: ({ cellData }) => (!isNaN(cellData) ? FormatData.formatBytes(cellData, 1) : null),
          },
          {
            dataKey: "id",
            label: t("Actions"),
            disableSort: true,
            width: 80,
            cellRenderer: ({ rowData }) => (
              <TaskFilesTableItemActions
                editMode={this.props.editMode}
                onRemove={this.props.canRemoveFile ? this.removeSingleFile : null}
                file={rowData}
              />
            ),
          },
        ];

        return (
          <div className="flex-column" style={{ minHeight: 490 }}>
            <Row justify="space-between">
              <TaskFilesTableActions {...this.tableActions} />
              {withButton && (
                <Col>
                  <ModalConsumer>
                    {({ showModal }) => (
                      <Button
                        type="primary"
                        faIcon="plus"
                        data-testid="task-files-add-project-file-button"
                        onClick={() =>
                          showModal(AttachProjectFiles, {
                            modalProps: {
                              title: t("Attach files from project"),
                              width: 800,
                              footer: null,
                              zIndex: 1013,
                            },
                            componentProps: { addFile: this.props.addFile },
                          })
                        }
                      >
                        {t("New project file")}
                      </Button>
                    )}
                  </ModalConsumer>
                </Col>
              )}
            </Row>
            <ScrollableTable<TaskUploadFile>
              stretchHeight={true}
              wrapperStyles={{ height: null, flex: "1 1 auto", minHeight: 270 }}
              rowKey="id"
              data={data}
              columns={columns}
              onSelectRows={this.handleSelectRows}
              selectedRowKeys={this.state.selectedFileKeys}
              rowClassName={this.getRowClassName}
            />
          </div>
        );
      }
    }
  )
);
